/**
 * Derived state, and how reducers talk to it.
 *
 * Projections live in different storage on each side of the wire: Dexie on a phone, Postgres
 * on the server. So reducers never touch storage directly. They read through a `Reader` and
 * return `Effect`s describing what should change, and each side applies those effects to its
 * own tables.
 *
 * That indirection is what lets exactly the same reducer code run in both places — which is
 * the requirement that matters, because the moment the client and the server compute stock
 * or cost differently, the books stop matching the shop floor and there is no way to tell
 * which one is lying.
 */

import type { Centavos, FrecuenciaPago, Milli, Role, Settings, UnitCost } from '@abastece/shared';
import type { JournalEntry } from './ledger/journal.js';

export interface Usuario {
  id: string;
  nombre: string;
  rol: Role;
  pinHash: string;
  activo: boolean;
  /** serverSeq of the last event to write this row. Decides last-write-wins across devices. */
  rev: number;
}

export type DeviceKind = 'mostrador' | 'telefono' | 'camion' | 'admin';

export interface Dispositivo {
  id: string;
  codigo: string;
  nombre: string;
  tipo: DeviceKind;
  activo: boolean;
  /**
   * Who is using it, as far as the log says: whoever registered it, then whoever signed in on it
   * last (`sesion.iniciada`). The owner's decisions are taken only from a device the owner is
   * using (`permisos.ts`). Absent on rows written before it was kept.
   */
  usuarioActual?: string | null;
  rev: number;
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

export interface Producto {
  id: string;
  /** What the counter says and the receipt prints. */
  nombre: string;
  /**
   * The same product in English, typed by a person, for the English screen. Null when nobody
   * has; optional because a device that has not replayed since this field arrived holds rows
   * written before it existed.
   */
  nombreEn?: string | null;
  categoria: string;
  /** Code of the unit stock is held in: 'unidad', 'libra', 'litro'. */
  unidadBase: string;
  esMedicamento: boolean;
  perecedero: boolean;
  registroSanitario: string | null;
  /** Several, because the same product in different packaging scans differently. */
  codigosBarras: string[];
  /**
   * SHA-256 of the product's photo, or null. The name is all the log carries; the bytes live
   * in the `fotos` table on each side and travel by `/api/fotos`, never by sync.
   */
  foto?: string | null;
  /**
   * Stock in base units at or below which it is time to buy more. Null or absent: none set, and
   * «Por comprar» goes by how fast it sells.
   */
  stockMinimo?: Milli | null;
  activo: boolean;
  rev: number;
}

export interface UnidadVenta {
  id: string;
  productoId: string;
  nombre: string;
  /** Base units in one of these, in milli. A 5 lb bag of a pound-based product is 5000. */
  factor: Milli;
  /**
   * This presentation's own barcodes. A product's codes ring up its smallest unit; a case's
   * code, printed on the box, rings up the case.
   */
  codigosBarras?: string[];
  rev: number;
}

export interface ListaPrecio {
  id: string;
  nombre: string;
  esPredeterminada: boolean;
  orden: number;
  activa: boolean;
  rev: number;
}

export interface Precio {
  /** `${unidadVentaId}:${listaId}` */
  id: string;
  unidadVentaId: string;
  listaId: string;
  precio: Centavos;
  rev: number;
}

/** Why an offer was put up (`oferta.creada`): stock that will expire first, stock not moving, or the owner's own. */
export type MotivoOferta = 'vence' | 'lento' | 'otro';

/**
 * What an offer gives a presentation: a lower price, or `lleva` of it for the price of `paga`
 * (2026-11-03: «2×1», «lleva 3, paga 2»), at the list's own price with the rest free.
 */
export type PrecioDeOferta = { unidadVentaId: string; precio: Centavos } | { unidadVentaId: string; lleva: number; paga: number };

/**
 * Una oferta (2026-11-03): a product's prices on one list, lower, from `desde` to `hasta`, both in.
 * While it lasts it is that list's price at the till (`ofertaDe` on the phone); ended by hand
 * (`oferta.terminada`), from `terminadaEl` on it is not.
 */
export interface Oferta {
  id: string;
  productoId: string;
  listaId: string;
  /** What each presentation gets during it, a lower price or so many for fewer; one not named keeps its own. */
  precios: PrecioDeOferta[];
  desde: string;
  hasta: string;
  motivo: MotivoOferta;
  nota: string | null;
  /** The day it was ended by hand, before its last; null while it runs its course. */
  terminadaEl: string | null;
  rev: number;
}

export interface Proveedor {
  id: string;
  nombre: string;
  telefono: string | null;
  contacto: string | null;
  notas: string | null;
  activo: boolean;
  rev: number;
}

// ---------------------------------------------------------------------------
// Inventario
// ---------------------------------------------------------------------------

export interface Existencia {
  /** `${productoId}:${ubicacion}` */
  id: string;
  productoId: string;
  ubicacion: string;
  cantidad: Milli;
  /** Costo promedio ponderado, in thousandths of a centavo per base unit. */
  costoPromedio: UnitCost;
  /**
   * When a count last established this stock, on the server's clock (`inventario.conteo`). A
   * movement from before it that reaches the books after it was already in what was counted
   * (`yaContado`). Only a count sets it: every other write keeps it as it was.
   */
  contadoEn?: string | null;
  /**
   * Id of the last thing that moved this stock.
   *
   * Exists so an annulment can tell whether it is still the most recent movement. Weighted
   * average is path-dependent: once a sale or a later purchase has shifted the average, there
   * is no honest way to subtract an earlier purchase back out.
   */
  ultimoMovimientoId: string;
  rev: number;
}

export interface Lote {
  id: string;
  productoId: string;
  numero: string;
  vencimiento: string;
  cantidadRecibida: Milli;
  /** What is left of this batch. Drawn down oldest-expiry-first as stock leaves. */
  cantidadDisponible: Milli;
  compraId: string;
  ubicacion: string;
  rev: number;
}

export type MotivoSalida = 'merma' | 'vencido' | 'consumo_interno' | 'robo';

export interface Reempaque {
  id: string;
  ubicacion: string;
  fecha: string;
  origenProductoId: string;
  origenCantidad: Milli;
  mermaCantidad: Milli;
  destinos: { productoId: string; cantidad: Milli; valorAsignado: Centavos }[];
  costoAdicional: Centavos;
  valorConsumido: Centavos;
  valorMerma: Centavos;
  rev: number;
}

export interface Conteo {
  id: string;
  ubicacion: string;
  fecha: string;
  lineas: {
    productoId: string;
    cantidadContada: Milli;
    /** What the books said on the counting device when the line was counted. */
    cantidadSistema: Milli;
    diferencia: Milli;
    valorDiferencia: Centavos;
    /** When it was counted, on the server's clock. */
    contadoEn: string;
    /** A later count of this product had already been applied: this one changed nothing. */
    superada?: boolean;
  }[];
  valorFaltante: Centavos;
  valorSobrante: Centavos;
  rev: number;
}

export interface Salida {
  id: string;
  ubicacion: string;
  fecha: string;
  productoId: string;
  cantidadBase: Milli;
  motivo: MotivoSalida;
  loteId: string | null;
  nota: string | null;
  costo: Centavos;
  rev: number;
}

export interface Traslado {
  id: string;
  origen: string;
  destino: string;
  fecha: string;
  lineas: { productoId: string; cantidadBase: Milli; costo: Centavos }[];
  valorTotal: Centavos;
  rev: number;
}

export type FormaPago =
  | 'efectivo_cordobas'
  | 'efectivo_dolares'
  | 'transferencia'
  | 'billetera'
  | 'credito';

export interface CompraLinea {
  productoId: string;
  cantidadBase: Milli;
  /** Line total in the purchase currency, excluding freight and IVA. */
  costoTotal: Centavos;
  /** This line's share of the freight, in the purchase currency. */
  fleteAsignado: Centavos;
  /** This line's share of the invoice's IVA, in the purchase currency. Absent: the purchase had none. */
  ivaAsignado?: Centavos;
  /** Landed cost in córdobas: line total plus freight and IVA, converted. This is what enters the average. */
  costoCordobas: Centavos;
  /** The stock this line landed on, kept so an annulment can restore it exactly. */
  antes: { cantidad: Milli; costoPromedio: UnitCost };
  /** Base units already sent back to the supplier. Absent on rows from before returns existed. */
  devuelta?: Milli;
  /**
   * The batch it came in, as the invoice said: a void takes it back out of that batch, and a return
   * to the supplier sends that batch back, not whichever expires first (2026-10-13). Absent: none,
   * or a row from before it was kept.
   */
  lote?: { numero: string; vencimiento: string };
}

export interface Compra {
  id: string;
  proveedorId: string;
  fecha: string;
  documento: string | null;
  moneda: 'NIO' | 'USD';
  tasaCambio: number | null;
  flete: Centavos;
  /** The invoice's IVA, in the purchase currency, part of the goods' cost. Absent or 0: none. */
  iva?: Centavos;
  ubicacion: string;
  formaPago: FormaPago;
  vencePago: string | null;
  /** The till it was paid out of, when it was (`compra.registrada`); undone into it while open. */
  sesionId?: string | null;
  /**
   * The bank account `formaPago` paid from, by transfer (`bancos.ts`): undone into it. Absent or
   * null: «Bancos», as every purchase before 2026-10-10.
   */
  cuenta?: string | null;
  /**
   * What `formaPago` paid from a dollar account, in its dollars, USD cents: undone with the
   * purchase, and moved with its cost when that is corrected. Absent otherwise.
   */
  dolares?: Centavos | null;
  lineas: CompraLinea[];
  totalCordobas: Centavos;
  /**
   * How much of it has actually been paid. Equals the total for anything paid on the spot;
   * starts at zero for a credit purchase and rises as the supplier is paid.
   */
  pagado: Centavos;
  /** What the goods sent back to the supplier were worth, however it was settled. */
  devuelto?: Centavos;
  /** The part of `devuelto` taken off what is owed. What is still owed is total − pagado − this. */
  devueltoACuenta?: Centavos;
  /** Each payment that named itself (`pagoId`), so it can be voided; `pagado` counts them all. */
  pagos?: PagoProveedor[];
  anulada: boolean;
  motivoAnulacion: string | null;
  /**
   * Its place in the log (`lugarEnElLog`), which a count of the money it was paid with has to come
   * after to have found it (2026-10-13). Absent or null on a purchase from before: as early as can be.
   */
  lugar?: number | null;
  /**
   * When it was entered, on the server's clock (`momentoDe`, ms): a count of its goods after this
   * already found what it brought (`existencia.contadoEn`), so a quantity corrected later leaves
   * the counted stock as it is (2026-10-13). Absent or null on a purchase with no goods.
   */
  momento?: number | null;
  rev: number;
}

/**
 * A payment to a supplier, of a purchase or of an expense on credit, kept with what it paid
 * (`compra.pagada`, `gasto.pagado` with a `pagoId`) so it can be voided. One voided stays, marked.
 * A purchase paid in parts keeps its parts here too (`conLaCompra`), and so does an expense paid in
 * parts since 2026-10-16 (`conElGasto`).
 */
export interface PagoProveedor {
  id: string;
  fecha: string;
  monto: Centavos;
  /** Dollars only in a part paid with the purchase: a payment after it is in córdobas. */
  formaPago: 'efectivo_cordobas' | 'efectivo_dolares' | 'transferencia' | 'billetera';
  /**
   * Paid with the purchase itself (`compra.registrada`'s `pagos`), not after it: undone with the
   * purchase, and on its own only while what the parts left is owed, as any payment of a debt.
   */
  conLaCompra?: true;
  /** Voided with its purchase (`compra.anulada`), not on its own. */
  anuladoConLaCompra?: true;
  /**
   * Paid with the expense itself (`gasto.registrado`'s `pagos`), as `conLaCompra` is with a
   * purchase: undone with the expense, and on its own only while what the parts left is owed.
   */
  conElGasto?: true;
  /** Voided with its expense (`gasto.anulado`), not on its own. */
  anuladoConElGasto?: true;
  /**
   * A part of an expense in dollars, as typed, USD cents (`monto` is what it came to): the rest's
   * dollars are the expense's less its parts', which a dollar account that paid the rest gets back
   * when the expense is voided. Kept here, in the parts the server keeps whole, as a column of its
   * own would not be.
   */
  dolares?: Centavos;
  /** The till it was paid out of, when it was. */
  sesionId: string | null;
  /** The córdoba account a payment by transfer came from; absent or null, «Bancos». */
  cuenta?: string | null;
  referencia: string | null;
  anulado: boolean;
  motivoAnulacion: string | null;
  /**
   * Its place in the log (`lugarEnElLog`), which a count of the money it came from has to come
   * after to have found it. Absent on payments named before places were kept: then `rev`.
   */
  lugar?: number;
  rev: number;
}

/** What is still owed on a purchase: nothing unless it was on credit. */
export function pendienteDeCompra(c: Compra): Centavos {
  if (c.formaPago !== 'credito' || c.anulada) return 0 as Centavos;
  return (c.totalCordobas - c.pagado - (c.devueltoACuenta ?? 0)) as Centavos;
}

/** What is still owed on an expense: nothing unless it was bought on credit (`gasto.pagado`). */
export function pendienteDeGasto(g: Pick<Gasto, 'formaPago' | 'anulado' | 'montoCordobas' | 'pagado'>): Centavos {
  if (g.formaPago !== 'credito' || g.anulado) return 0 as Centavos;
  return (g.montoCordobas - (g.pagado ?? 0)) as Centavos;
}

/**
 * A debt carried over from before the shop kept its books here (`compra.saldo_inicial`): a
 * purchase on credit with no goods. Every purchase that brought goods has at least one line.
 */
export function esDeudaAnterior(c: Pick<Compra, 'lineas'>): boolean {
  return c.lineas.length === 0;
}

// ---------------------------------------------------------------------------
// Caja y ventas
// ---------------------------------------------------------------------------

export type FormaPagoVenta =
  | 'efectivo_cordobas'
  | 'efectivo_dolares'
  | 'transferencia'
  | 'billetera'
  | 'tarjeta'
  /** Fiado: nothing changes hands, and the client owes it. */
  | 'credito';

export interface SesionCaja {
  id: string;
  usuarioId: string;
  dispositivoId: string;
  ubicacion: string;
  abiertaEn: string;
  cerradaEn: string | null;
  fondoInicial: Centavos;
  estado: 'abierta' | 'cerrada';

  /** Running totals, so closing the shift is O(1) rather than a scan of the day's sales. */
  efectivoCordobas: Centavos;
  /** Dollars taken in, in USD cents — what should physically be in the drawer. */
  efectivoDolares: Centavos;
  otrosPagos: Centavos;
  cambioEntregado: Centavos;
  retiros: Centavos;
  ingresos: Centavos;
  ventas: number;
  totalVendido: Centavos;
  costoVendido: Centavos;
  /**
   * Sold on fiado this shift. Part of `totalVendido`, and none of it is in the drawer. Absent on
   * rows written before fiado existed, like the two below.
   */
  fiado?: Centavos;
  /** Cash clients paid off what they owe, into this drawer. The corte expects it. */
  abonos?: Centavos;
  /**
   * Cash handed back to customers out of this drawer: for returns, and for voided sales whose own
   * till had already closed. The corte expects it gone.
   */
  devoluciones?: Centavos;
  /**
   * Dollars in the drawer at opening, and dollars taken out or put in during the shift, in USD
   * cents. Absent on rows written before a drawer could open with dollars in it.
   */
  fondoDolares?: Centavos;
  retirosDolares?: Centavos;
  ingresosDolares?: Centavos;

  conteoCordobas: Centavos | null;
  conteoDolares: Centavos | null;
  diferenciaCordobas: Centavos | null;
  diferenciaDolares: Centavos | null;

  /**
   * The float kept on the books (see `caja.abierta` and `caja.cerrada`). At opening: the corte
   * whose float this drawer took over, córdobas brought from general cash, what the count should
   * have found — the two together — and how far it was from it. At the corte: the córdobas left
   * for the next shift, and the córdobas and dollars taken out to general cash, which stay null
   * until then. Absent on rows written before these existed.
   */
  relevoDe?: string | null;
  deGeneral?: Centavos | null;
  esperadoApertura?: Centavos | null;
  diferenciaApertura?: Centavos | null;
  queda?: Centavos | null;
  entregado?: Centavos | null;
  entregadoDolares?: Centavos | null;
  /**
   * What the shift's dollars are carried at in «Caja dólares», in córdobas: each dollar at the
   * rate it was booked in at, rounded as it was booked. The corte takes out exactly this, so the
   * account ends every shift at nothing, and the gap to what the dollars are worth that day is
   * a gain or a loss on exchange.
   */
  dolaresContables?: Centavos;
  /**
   * What this till handed over that nobody has received yet (`EFECTIVO_POR_RECIBIR`): córdobas,
   * dollars in USD cents, and what those dollars went onto the books at. Zero once received.
   */
  porRecibir?: Centavos;
  porRecibirDolares?: Centavos;
  porRecibirValor?: Centavos;
  /** The last receipt: who counted it, when, and how far off it was, in córdobas. */
  recibidoPor?: string | null;
  recibidoEn?: string | null;
  diferenciaRecibida?: Centavos;
  /** Purchases, expenses and supplier payments paid out of this drawer. The corte expects them gone. */
  pagado?: Centavos;
  /**
   * Who has the drawer now: whoever received it last (`caja.entregada`), or null while it is
   * still with whoever opened it (`usuarioId`). What the corte finds is theirs.
   */
  responsableId?: string | null;
  /**
   * What the handovers found against what was expected, each currency on its own, which the
   * drawer then went on from: the expected cash moves by it (`efectivoEsperado`), so the next
   * count measures only the shift since.
   */
  ajusteCordobas?: Centavos;
  ajusteDolares?: Centavos;
  /** The handovers' differences together, in córdobas, dollars at the official rate. */
  diferenciaEntregas?: Centavos;
  /** Who did the corte, which is not always whoever opened. */
  cerradaPor?: string | null;
  /**
   * A first count that was off and was redone (`cajaReconteoDesde`): the córdobas first counted at
   * opening, and the córdobas and dollars first counted at the corte. The counts above are the
   * second ones. Null when the first count stood; absent on rows written before recounts.
   */
  primerConteoApertura?: Centavos | null;
  primerConteoCordobas?: Centavos | null;
  primerConteoDolares?: Centavos | null;
  /**
   * The same at a handover (`caja.entregada`), each currency on its own: the latest one counted
   * twice, if a till changed hands more than once.
   */
  primerConteoEntregaCordobas?: Centavos | null;
  primerConteoEntregaDolares?: Centavos | null;
  /**
   * The place in the log of the last handover (`lugarEnElLog`), which counted the drawer: a
   * supplier's payment out of it from before is past voiding (`compra.pago_anulado`), as that
   * count already found it. Absent on rows from before payments could be voided.
   */
  entregadaRev?: number | null;
  /**
   * When the last handover was (`clientTs`): the drawer was counted then, and whoever has it has
   * had it since. Absent on rows from before it was kept.
   */
  entregadaEn?: string | null;
  /** The same when the owner received what it handed over (`efectivo.recibido`). */
  primerConteoRecibidoCordobas?: Centavos | null;
  primerConteoRecibidoDolares?: Centavos | null;
  rev: number;
}

export interface VentaLinea {
  productoId: string | null;
  descripcion: string;
  unidadVentaId: string | null;
  cantidad: Milli;
  factor: Milli;
  precioLista: Centavos;
  /** The list `precioLista` came from, when it is not the sale's (2026-10-11). Absent: the sale's. */
  listaId?: string;
  precioUnitario: Centavos;
  motivoOverride: string | null;
  autorizadoPor: string | null;
  /**
   * The offer `precioLista` came from (2026-11-03), and the list's own price without it. Absent on a
   * line that was at no offer.
   */
  ofertaId?: string;
  precioRegular?: Centavos;
  subtotal: Centavos;
  /** Cost actually posted for this line. Written by the reducer, not by the device. */
  costoPosteado: Centavos;
  /** Cost per base unit used for that posting — what a reversal puts the goods back at. */
  costoUnitarioPosteado: UnitCost;
  /** Sale units already returned. Absent until something is. */
  devuelta?: Milli;
}

export interface VentaPago {
  forma: FormaPagoVenta;
  /** Value applied to the bill, in córdobas. */
  monto: Centavos;
  /**
   * What was actually debited to the asset account.
   *
   * Differs from `monto` only for dollars: the drawer is carried at the official rate while
   * the customer was credited at the store rate, and the gap is FX gain or loss. Stored so a
   * reversal undoes exactly what the sale did.
   */
  montoContable: Centavos;
  montoOriginal: Centavos | null;
  tasaCambio: number | null;
  referencia: string | null;
  estado: 'confirmado' | 'pendiente' | 'rechazado';
  /** For fiado past the client's limit: the owner who allowed it. */
  autorizadoPor?: string | null;
  /**
   * A transfer or a wallet payment that never arrived and went on the client's account instead
   * (`pago.rechazado` with `aFiado`): it is fiado now, and this says how it started.
   */
  era?: 'transferencia' | 'billetera';
  /**
   * The córdoba account a card payment or a confirmed transfer went to (`bancos.ts`): a void takes
   * it back out of there. Absent or null: «Bancos».
   */
  cuenta?: string | null;
  /**
   * Of a transfer that never arrived (`rechazado`), what came back as goods since (`repartirDevolucion`):
   * the loss it booked, undone that far.
   */
  recuperado?: Centavos;
}

export interface Venta {
  id: string;
  ticket: string;
  sesionId: string;
  fecha: string;
  ubicacion: string;
  listaId: string;
  clienteId: string | null;
  /** The preventa order this sale fulfilled, if any. */
  pedidoId: string | null;
  lineas: VentaLinea[];
  descuento: Centavos;
  /** What the customer paid for the order being brought, already in `total`. Absent when none. */
  envio?: Centavos;
  redondeo: Centavos;
  total: Centavos;
  pagos: VentaPago[];
  cambio: Centavos;
  costoTotal: Centavos;
  /** Refunded through returns so far. Absent until something comes back. */
  devuelto?: Centavos;
  anulada: boolean;
  motivoAnulacion: string | null;
  rev: number;
}

/** The part of a sale that was fiado. */
export function fiadoDeVenta(v: Venta): Centavos {
  return v.pagos.reduce((a, p) => (p.forma === 'credito' ? a + p.monto : a), 0) as Centavos;
}

/**
 * The cash a sale took in, less the change handed back, in córdobas: dollars at what the customer
 * was credited for them. What a void owes the customer in cash.
 */
export function efectivoDeVenta(v: Venta): Centavos {
  const recibido = v.pagos.reduce(
    (a, p) =>
      p.estado !== 'rechazado' && (p.forma === 'efectivo_cordobas' || p.forma === 'efectivo_dolares')
        ? a + p.monto
        : a,
    0,
  );
  return Math.max(0, recibido - v.cambio) as Centavos;
}

/**
 * The money a sale took in that is in hand, less the change: cash, card, and a transfer or a
 * wallet payment somebody saw arrive. Not fiado, and not what still waits in the queue or never
 * came. What a return can give back as money (`devoluciones.ts`).
 */
export function dineroDeVenta(v: Pick<Venta, 'pagos' | 'cambio'>): Centavos {
  const recibido = v.pagos.reduce((a, p) => {
    if (p.forma === 'credito' || p.estado === 'rechazado') return a;
    if ((p.forma === 'transferencia' || p.forma === 'billetera') && p.estado !== 'confirmado') return a;
    return a + p.monto;
  }, 0);
  return Math.max(0, recibido - v.cambio) as Centavos;
}

/** Phone credit on hand, carried at cost and averaged exactly like inventory. */
export interface SaldoTelefonico {
  /** The operator name, lowercased. There is one balance per operator. */
  id: string;
  operadora: string;
  facial: Centavos;
  costo: Centavos;
  rev: number;
}

export interface Recarga {
  id: string;
  sesionId: string;
  operadora: string;
  telefono: string;
  montoFacial: Centavos;
  costo: Centavos;
  comision: Centavos;
  formaPago: string;
  fecha: string;
  /**
   * Paid by transfer or wallet, it waits in the queue like a sale's payment until somebody sees
   * the money. Cash is confirmed as it is taken.
   */
  estadoPago: 'confirmado' | 'pendiente' | 'rechazado';
  referencia: string | null;
  /** The córdoba account a transfer was seen in when confirmed; absent or null, «Bancos». */
  cuenta?: string | null;
  rev: number;
}

// ---------------------------------------------------------------------------
// Clientes y reparto
// ---------------------------------------------------------------------------

export interface Cliente {
  id: string;
  nombre: string;
  tipo: 'mostrador' | 'mayorista';
  telefono: string | null;
  /** Free text. Most of Nicaragua navigates by landmark, not by street. */
  direccion: string | null;
  /** Price tier. Null falls back to the default list. */
  listaId: string | null;
  /** Day the route passes: 0 = domingo. */
  diaVisita: number | null;
  notas: string | null;
  /** Fiado: the most they may owe without the owner. Null or absent: every fiado takes the owner. */
  limiteCredito?: Centavos | null;
  /**
   * What they owe right now. Negative is money in their favour, after returning something they
   * had already paid for. Absent on rows written before fiado existed, which owe nothing.
   */
  saldo?: Centavos;
  /**
   * What they already owed before the shop kept fiado here (`cliente.saldo_inicial`), part of
   * `saldo`. Absent: nothing carried over.
   */
  saldoInicial?: Centavos;
  /** The day it was first written down, which a correction keeps. Null once it goes back to zero. */
  saldoInicialFecha?: string | null;
  /**
   * Of what the owner gave up on (`incobrable.registrado`), what still stands: not undone, and not
   * come back since. It comes back when something takes more off what they owe than they owe — an
   * abono, a void, a return to the account, an opening balance lowered (`bajarDeuda`): that part
   * was never theirs to have in their favour, it was the fiado given up on (2026-10-13). Absent: none.
   */
  perdido?: Centavos;
  /**
   * What came back that way and still stands, so an abono that brought some back and is voided
   * after gives back no more than is still there (`volverAPerder`). Absent: none.
   */
  recuperado?: Centavos;
  /** The cédula's number, as typed. Absent on rows from before it was asked for. */
  cedula?: string | null;
  /** SHA-256 of a photo of the cédula, which travels like a product's photo. */
  fotoCedula?: string | null;
  activo: boolean;
  rev: number;
}

/** A client paying off some of what they owe. */
export interface Abono {
  id: string;
  clienteId: string;
  fecha: string;
  monto: Centavos;
  formaPago: 'efectivo_cordobas' | 'transferencia' | 'billetera';
  /** The till the cash went into, or null: general cash, a bank, a wallet. */
  sesionId: string | null;
  /** The córdoba account a transfer went to; absent or null, «Bancos». */
  cuenta?: string | null;
  referencia: string | null;
  nota: string | null;
  anulado: boolean;
  motivoAnulacion: string | null;
  /**
   * A transfer or a wallet payment taken by somebody who does not confirm them waits in the queue
   * («Transferencias»), its money in «Transferencias pendientes», until somebody sees it arrive
   * (`confirmado`) or not (`rechazado`, and the client owes it again). Absent or null: in hand.
   */
  estadoPago?: 'pendiente' | 'confirmado' | 'rechazado' | null;
  /**
   * The part of it that paid what had been given up on (`Cliente.perdido`), out of «Fiado
   * incobrable» rather than off what they owe. Absent: none.
   */
  recuperado?: Centavos | null;
  rev: number;
}

/**
 * Fiado given up on (`incobrable.registrado`): off what the client owes and into «Fiado
 * incobrable». Undone (`anulado`), the client owes it again.
 */
export interface Incobrable {
  id: string;
  clienteId: string;
  fecha: string;
  monto: Centavos;
  motivo: string;
  anulado: boolean;
  motivoAnulacion: string | null;
  rev: number;
}

/**
 * Cash taken from general cash to the bank (`efectivo.depositado`): córdobas to the córdoba
 * account, dollars to the dollar account, which keeps them in dollars for its statement. Or, with
 * `delBanco`, brought from the bank to general cash (`efectivo.sacado_del_banco`).
 */
export interface Deposito {
  id: string;
  fecha: string;
  cordobas: Centavos;
  /** USD cents. */
  dolares: Centavos;
  /** What the dollars went onto the books at, in córdobas: the official rate of the day. */
  valorDolares: Centavos;
  /** The córdoba account the córdobas went to or came from (`bancos.ts`); absent or null, «Bancos». */
  cuenta?: string | null;
  /** The dollar account the dollars went to or came from; absent or null, «Banco en dólares». */
  cuentaDolares?: string | null;
  /** The deposit slip's number. */
  referencia: string | null;
  nota: string | null;
  /**
   * Brought from the bank to general cash, the other way (`efectivo.sacado_del_banco`). Absent or
   * false on a deposit.
   */
  delBanco?: boolean;
  /** A deposit that was not (`efectivo.deposito_anulado`). Absent on rows from before voids. */
  anulado?: boolean;
  motivoAnulacion?: string | null;
  /**
   * Its place in the log (`lugarEnElLog`), which a count of the safe or a statement has to come
   * after to have found it. Absent on rows from before places were kept: then `rev`, which only
   * its void moves.
   */
  lugar?: number | null;
  rev: number;
}

export interface DevolucionLinea {
  /** Position of the line in the sale. */
  indice: number;
  productoId: string | null;
  descripcion: string;
  /** Sale units returned, milli. */
  cantidad: Milli;
  factor: Milli;
  reingresar: boolean;
  /** What the customer gets back for it, the sale's discount taken off. */
  valor: Centavos;
  /** What it cost, at the cost it was sold at. */
  costo: Centavos;
}

/** Part of a sale coming back. */
export interface Devolucion {
  id: string;
  ventaId: string;
  ticket: string;
  clienteId: string | null;
  fecha: string;
  /** The till the cash refund came out of. */
  sesionId: string | null;
  lineas: DevolucionLinea[];
  /**
   * What went back the way `reembolso` says. The goods' worth, all but what went against a transfer
   * that never arrived or back to «Redondeo» (`repartirDevolucion`); each line keeps its own.
   */
  total: Centavos;
  reembolso: 'efectivo_cordobas' | 'credito' | 'transferencia' | 'billetera';
  /** The córdoba account a refund by transfer went out of; absent or null, «Bancos». */
  cuenta?: string | null;
  motivo: string;
  rev: number;
}

/** Goods going back to a supplier. */
export interface DevolucionCompra {
  id: string;
  compraId: string;
  proveedorId: string;
  fecha: string;
  lineas: {
    indice: number;
    productoId: string;
    cantidadBase: Milli;
    /** What the supplier credits for it: what the purchase paid, freight included. */
    valor: Centavos;
    /** What it was carried at in stock when it left. */
    costo: Centavos;
  }[];
  total: Centavos;
  liquidacion: 'descontar' | 'efectivo_cordobas' | 'transferencia' | 'billetera';
  /** The córdoba account the supplier's money came back to, by transfer; absent or null, «Bancos». */
  cuenta?: string | null;
  motivo: string;
  rev: number;
}

export type EstadoPedido = 'pendiente' | 'entregado' | 'cancelado';

export interface Pedido {
  id: string;
  clienteId: string;
  fecha: string;
  fechaEntrega: string;
  listaId: string;
  lineas: {
    productoId: string;
    unidadVentaId: string;
    cantidad: Milli;
    precioUnitario: Centavos;
    subtotal: Centavos;
    /**
     * The list the line's price came from, when it is not the order's (2026-10-18), as on a sale's
     * line. Absent, the order's `listaId`.
     */
    listaId?: string;
    /**
     * Why the price agreed is not the list's, and the owner who allowed one below the margin floor,
     * as on a sale's line (`pedido.tomado`). Absent on a line at the list's price, and on an order
     * taken before 2026-10-05 or on a phone that did not know them yet.
     */
    motivoOverride?: string;
    autorizadoPor?: string;
  }[];
  /** The goods: what the lines come to. */
  total: Centavos;
  /**
   * What the customer pays for having it brought, on top of `total` (`pedido.tomado`, 2026-10-12).
   * Absent when there is none, and on an order taken before or on a phone that did not know it.
   */
  envio?: Centavos;
  estado: EstadoPedido;
  /** The sale that fulfilled it. */
  ventaId: string | null;
  motivoCancelacion: string | null;
  nota: string | null;
  rev: number;
}

export type MotivoLiquidacion =
  | 'despido_sin_causa'
  | 'renuncia'
  | 'mutuo_acuerdo'
  | 'despido_con_causa';

export interface Empleado {
  id: string;
  nombre: string;
  cedula: string | null;
  cargo: string;
  salarioMensual: Centavos;
  fechaIngreso: string;
  fechaSalida: string | null;
  inssNumero: string | null;
  telefono: string | null;
  /** Optional link to a login. Plenty of employees never touch the system. */
  usuarioId: string | null;
  formaPago: 'efectivo_cordobas' | 'transferencia' | 'billetera';
  /** Aguinaldo owed right now — what a liquidación would have to pay out today. */
  aguinaldoAcumulado: Centavos;
  vacacionesAcumuladas: Centavos;
  /** Milésimas de día, so 2.5 días al mes stays an integer. */
  diasVacacionesAcumulados: number;
  /** Salary handed over in advance and not yet taken off a payroll (`empleado.adelanto`). */
  adelantosPendientes?: Centavos;
  /**
   * How they are paid (2026-10-12; `planilla.ts` in shared): how often, a fixed amount or so much
   * a day, and whether INSS and the benefits apply. Absent on rows written before any of it
   * existed: every month, the monthly salary, INSS and benefits.
   */
  frecuencia?: FrecuenciaPago;
  porDia?: boolean;
  /** For each day worked, or for each week or fifteen days; null when it is `salarioMensual` a month. */
  tarifa?: Centavos | null;
  conInss?: boolean;
  /** Aguinaldo and vacations accrue, and severance is paid on leaving. */
  conPrestaciones?: boolean;
  activo: boolean;
  motivoSalida: MotivoLiquidacion | null;
  rev: number;
}

export interface PlanillaLinea {
  empleadoId: string;
  /** Snapshotted: a payslip from March has to keep saying what it said in March. */
  nombre: string;
  cargo: string;
  bruto: Centavos;
  otrosGravables: Centavos;
  vacacionesPagadas: Centavos;
  diasVacaciones: number;
  aguinaldoPagado: Centavos;
  indemnizacion: Centavos;
  gravable: Centavos;
  exento: Centavos;
  brutoTotal: Centavos;
  inssLaboral: Centavos;
  irRetenido: Centavos;
  neto: Centavos;
  inssPatronal: Centavos;
  inatec: Centavos;
  provisionAguinaldo: Centavos;
  provisionVacaciones: Centavos;
  /** Advances taken off this line's net: what is handed over is `neto` less this. */
  adelantoDescontado?: Centavos;
  /** Days worked, in thousandths, for somebody paid by the day. */
  dias?: number;
  /**
   * Of what this line paid, what earlier runs had set aside for it: the aguinaldo and the vacation
   * paid out of what was owed, and the salary of vacation days rested (2026-10-20). The entry takes
   * it off what is owed, not out of the expense (`costoDeLinea`). Absent on lines from before.
   */
  deLoApartado?: Centavos;
  /**
   * `deLoApartado` in its parts, and the vacation days the line earned (2026-10-31): what a
   * correction takes back exactly (`revertirLinea`). Absent on lines from before, worked out again.
   */
  contraAguinaldo?: Centavos;
  contraVacaciones?: Centavos;
  descansadas?: Centavos;
  diasGanados?: number;
  /** Of `diasVacaciones`, the days it took off what they had, never past it (2026-10-31). */
  diasTomados?: number;
}

export interface Planilla {
  id: string;
  /** `YYYY-MM`: the month the run belongs to, the month of `hasta`. */
  periodo: string;
  fecha: string;
  /** Whose: the people paid that often. Absent on runs from before: every month. */
  frecuencia?: FrecuenciaPago;
  /** The days it pays. Absent on runs from before, and on settlements: the whole month. */
  desde?: string | null;
  hasta?: string | null;
  /** True for the settlement of a single employee leaving, false for the monthly run. */
  esLiquidacion: boolean;
  lineas: PlanillaLinea[];
  totalGravable: Centavos;
  totalExento: Centavos;
  totalInssLaboral: Centavos;
  totalIr: Centavos;
  totalNeto: Centavos;
  totalInssPatronal: Centavos;
  totalInatec: Centavos;
  /** What the month really cost the business, employer contributions included. */
  costoTotal: Centavos;
  /** Advances taken off the nets: the payment hands over `totalNeto` less this. */
  totalAdelantos?: Centavos;
  pagada: boolean;
  pagadaEn: string | null;
  nota: string | null;
  rev: number;
}

export interface PagoCotizaciones {
  id: string;
  fecha: string;
  periodo: string;
  inss: Centavos;
  inatec: Centavos;
  ir: Centavos;
  total: Centavos;
  cuenta: string;
  referencia: string | null;
  rev: number;
}

/** One person's aguinaldo on a payment of it (`aguinaldo.pagado`). */
export interface LineaDeAguinaldo {
  empleadoId: string;
  /** Snapshotted, as on a run's line. */
  nombre: string;
  monto: Centavos;
  /** What the runs had set aside for them when it was paid, never below nothing. */
  apartado: Centavos;
  /** Of `monto`, what came out of `apartado`; the rest was an expense of the payment. */
  deLoApartado: Centavos;
}

/** The aguinaldo paid on its own (2026-10-31); with no lines, voided (`aguinaldo.corregido`). */
export interface PagoAguinaldo {
  id: string;
  fecha: string;
  lineas: LineaDeAguinaldo[];
  total: Centavos;
  formaPago: 'efectivo_cordobas' | 'transferencia' | 'billetera';
  /** What it came out of (`cuentaPago`). */
  cuenta: string;
  referencia: string | null;
  /** Why it was last set right. */
  nota: string | null;
  /** Its place in the log (`lugarEnElLog`): a count after it already found the money it paid with. */
  lugar: number;
  rev: number;
}

export type CategoriaGasto =
  | 'alquiler'
  | 'energia'
  | 'agua'
  | 'telefono_internet'
  | 'transporte'
  | 'mantenimiento'
  | 'impuestos_municipales'
  | 'empaques'
  | 'sueldos'
  | 'otros';

export interface Gasto {
  id: string;
  fecha: string;
  categoria: CategoriaGasto;
  descripcion: string;
  moneda: 'NIO' | 'USD';
  /** Amount in the currency it was paid in. */
  monto: Centavos;
  tasaCambio: number | null;
  /** What it cost in córdobas, which is what the ledger carries. */
  montoCordobas: Centavos;
  formaPago: FormaPago;
  /**
   * What has been paid of it, when it was bought on credit (`gasto.pagado`); the rest is still
   * owed (`pendienteDeGasto`). Absent until a first payment, and on anything paid on the spot.
   */
  pagado?: Centavos;
  /** The till it was paid out of, when it was; undone into it while open. */
  sesionId?: string | null;
  /**
   * The bank account a transfer came from (`bancos.ts`): a dollar account's paid `monto` in its
   * dollars. Undone into it. Absent or null: «Bancos».
   */
  cuenta?: string | null;
  proveedorId: string | null;
  referencia: string | null;
  nota: string | null;
  /** The receipt's photo, by its SHA-256 (`fotos`). */
  foto?: string | null;
  /**
   * A shortage explained (`faltante.explicado`): the count that found missing the money this was
   * paid with, and the account it booked that on. No money moved: it comes off the shortage.
   */
  faltanteDe?: string | null;
  faltanteCuenta?: 'FALTANTE_CAJA' | 'FALTANTE_GENERAL' | null;
  /**
   * Each payment of one bought on credit that named itself (`pagoId`), so it can be voided; and the
   * parts it was paid in, when it was (`conElGasto`), each in córdobas.
   */
  pagos?: PagoProveedor[];
  anulado: boolean;
  motivoAnulacion: string | null;
  /**
   * Its place in the log, as a deposit's (`Deposito.lugar`): a count of the money it was paid with
   * after it already found it. Absent on rows from before places were kept: then `rev`, which
   * only its void, or a payment of one on credit, moves.
   */
  lugar?: number | null;
  rev: number;
}

/** Owner money in or out. Equity, never income and never an expense. */
export interface MovimientoDueno {
  id: string;
  fecha: string;
  tipo: 'retiro' | 'aporte';
  monto: Centavos;
  /** The account's key: general cash, a bank account (`bancos.ts`) or the wallet. */
  cuenta: string;
  /** USD cents, into or out of a dollar account: `monto` is what they came to. */
  dolares?: Centavos | null;
  motivo: string | null;
  /** Money for or from the owner that was not (`dueno.movimiento_anulado`). Absent before voids. */
  anulado?: boolean;
  motivoAnulacion?: string | null;
  /** Its place in the log, as a deposit's (`Deposito.lugar`). */
  lugar?: number | null;
  rev: number;
}

/**
 * Money moved between two bank accounts (`banco.transferido`): what left one, in its currency, what
 * reached the other, in its, and what each went onto the books at.
 */
export interface TransferenciaBanco {
  id: string;
  fecha: string;
  /** Bank account keys (`bancos.ts`). */
  desde: string;
  hacia: string;
  /** What left `desde`, in its currency: córdoba centavos or USD cents. */
  monto: Centavos;
  /** What reached `hacia`, in its currency: `monto` itself between accounts of the same one. */
  recibido: Centavos;
  /** In córdobas: what `desde` gave up and `hacia` took on, on the books. Their gap is the exchange. */
  valorDesde: Centavos;
  valorHacia: Centavos;
  referencia: string | null;
  nota: string | null;
  anulado?: boolean;
  motivoAnulacion?: string | null;
  /** Its place in the log, as a deposit's (`Deposito.lugar`). */
  lugar?: number | null;
  rev: number;
}

export interface Jornada {
  id: string;
  camion: string;
  repartidorId: string;
  sesionId: string;
  fecha: string;
  estado: 'abierta' | 'liquidada';
  /** Value of goods loaded onto the truck while this run was open. */
  valorCargado: Centavos;
  /** Value returned to the warehouse before settlement. */
  valorDevuelto: Centavos;
  /** Shortfall found when the truck was counted at settlement. */
  faltanteMercaderia: Centavos;
  /** Shortfall found when the cash was counted. */
  faltanteEfectivo: Centavos;
  liquidadaEn: string | null;
  nota: string | null;
  rev: number;
}

/** Everything a reducer is allowed to read. Implemented over Dexie on the client, Postgres on the server. */
export interface Reader {
  usuario(id: string): Promise<Usuario | undefined>;
  /** Anybody at all, active or not: the very first person creates themselves (`permisos.ts`). */
  hayUsuarios(): Promise<boolean>;
  /**
   * An account's balance as the ledger stands, debit minus credit; with `hasta`, only entries
   * dated that day or before. For counting general cash against the books, and squaring the bank
   * against a statement, which shows the balance on its own date and not on the day it is read.
   */
  saldoCuenta(codigo: string, hasta?: string): Promise<Centavos>;
  /**
   * A dollar account's dollars as the ledger stands (`JournalLine.dolares`), USD cents; with
   * `hasta`, only entries dated that day or before, as `saldoCuenta`.
   */
  saldoCuentaDolares(codigo: string, hasta?: string): Promise<Centavos>;
  usuarioPorNombre(nombre: string): Promise<Usuario | undefined>;
  dispositivo(id: string): Promise<Dispositivo | undefined>;
  dispositivoPorCodigo(codigo: string): Promise<Dispositivo | undefined>;
  config(): Promise<Settings>;

  producto(id: string): Promise<Producto | undefined>;
  unidadVenta(id: string): Promise<UnidadVenta | undefined>;
  /** One product's presentations: the one named after its unit is renamed with it (`reduceProducto`). */
  unidadesDeProducto(productoId: string): Promise<UnidadVenta[]>;
  listaPrecio(id: string): Promise<ListaPrecio | undefined>;
  proveedor(id: string): Promise<Proveedor | undefined>;
  existencia(productoId: string, ubicacion: string): Promise<Existencia | undefined>;
  compra(id: string): Promise<Compra | undefined>;
  sesionCaja(id: string): Promise<SesionCaja | undefined>;
  /** Used to refuse a second open shift on the same terminal, which would wreck the corte. */
  sesionAbiertaDe(dispositivoId: string): Promise<SesionCaja | undefined>;
  venta(id: string): Promise<Venta | undefined>;
  /**
   * The sales made at `ubicacion` dated `desde` or later with a line of `productoId`, voided ones
   * too, in primary key order: the ones a purchase's corrected quantity costs again
   * (`compra.cantidad_corregida`).
   */
  ventasDeProducto(productoId: string, ubicacion: string, desde: string): Promise<Venta[]>;
  /**
   * The purchases into `ubicacion` with a line of `productoId`, voided ones too, in primary key
   * order: whether a later one left the same cost, which would make its sales and a corrected
   * one's indistinguishable (`compra.cantidad_corregida`).
   */
  comprasDeProducto(productoId: string, ubicacion: string): Promise<Compra[]>;
  saldoTelefonico(operadora: string): Promise<SaldoTelefonico | undefined>;
  recarga(id: string): Promise<Recarga | undefined>;
  /** Batches with stock left, for FEFO consumption and the expiry board. */
  lotesDe(productoId: string, ubicacion: string): Promise<Lote[]>;
  cliente(id: string): Promise<Cliente | undefined>;
  pedido(id: string): Promise<Pedido | undefined>;
  /** The orders still to deliver: what sets stock aside (`apartados.ts`). */
  pedidosPendientes(): Promise<Pedido[]>;
  jornada(id: string): Promise<Jornada | undefined>;
  /** Used so a load or a return finds the run it belongs to. */
  jornadaAbiertaDe(camion: string): Promise<Jornada | undefined>;
  /** Everything sitting in one place — what settlement has to bring back. */
  existenciasDe(ubicacion: string): Promise<Existencia[]>;
  /** Every location's stock of one product: where else its cost is known. */
  existenciasDeProducto(productoId: string): Promise<Existencia[]>;
  gasto(id: string): Promise<Gasto | undefined>;
  empleado(id: string): Promise<Empleado | undefined>;
  /** Everyone still on the payroll, for a run that names no one in particular. */
  empleadosActivos(): Promise<Empleado[]>;
  planilla(id: string): Promise<Planilla | undefined>;
  /**
   * The runs of these months, settlements aside: what refuses paying somebody twice for the same
   * day, and what a day worker's severance averages their pay over.
   */
  planillasDePeriodos(periodos: readonly string[]): Promise<Planilla[]>;
  pagoCotizaciones(id: string): Promise<PagoCotizaciones | undefined>;
  pagoAguinaldo(id: string): Promise<PagoAguinaldo | undefined>;
  oferta(id: string): Promise<Oferta | undefined>;
  /** A product's offers, ended ones too, in primary key order: what refuses two at once (`oferta.creada`). */
  ofertasDeProducto(productoId: string): Promise<Oferta[]>;
  movimientoDueno(id: string): Promise<MovimientoDueno | undefined>;
  abono(id: string): Promise<Abono | undefined>;
  incobrable(id: string): Promise<Incobrable | undefined>;
  /** Every write-off of one client, by id, undone ones too: what still stands of each (`perdidoQueSigue`). */
  incobrablesDe(clienteId: string): Promise<Incobrable[]>;
  deposito(id: string): Promise<Deposito | undefined>;
  transferenciaBanco(id: string): Promise<TransferenciaBanco | undefined>;
  devolucion(id: string): Promise<Devolucion | undefined>;
  /** Every return of one sale, by id: what came back of it, and how its money went back. */
  devolucionesDeVenta(ventaId: string): Promise<Devolucion[]>;
  devolucionCompra(id: string): Promise<DevolucionCompra | undefined>;
  /** What one event wrote on the books: what a count found missing, for a shortage explained. */
  asientosDeEvento(eventoId: string): Promise<JournalEntry[]>;
  /** The expenses that explain what one count found missing (`faltante.explicado`), voided ones too. */
  gastosDeFaltante(eventoId: string): Promise<Gasto[]>;
  /**
   * One event in the log, refused ones too: what kind it is and when it was made; null for none.
   * Only a count's shortage is explained, and on that count's day.
   */
  eventoDelLog(eventoId: string): Promise<EventoDelLog | null>;
  /**
   * One of a till's counts in the log, its opening or its corte, for the owner correcting it
   * (`caja.apertura_corregida`, `caja.corte_corregido`); null for any other event, or none. Refused
   * ones too: the books tell the count that stood by the till itself (`esElConteo`).
   */
  conteoDeCaja(eventoId: string): Promise<ConteoDeCaja | null>;
  /** The shift that took over what a corte left in the drawer (`relevoDe`), if one has opened. */
  relevoDe(sesionId: string): Promise<SesionCaja | undefined>;
  /**
   * The first count the books took of some money between two places in the log (`lugarEnElLog`),
   * both left out: general cash's (`efectivo.arqueo`), or one till's drawer, at a handover
   * (`caja.entregada`), at its corte (`caja.cerrada`) or, a route's, at its settlement
   * (`jornada.liquidada`). It found what that money really was, so a correction of something
   * before it moves what it found instead of the money (`projections/encontrado.ts`). Refused
   * counts are not counts. Null when none was taken between them.
   */
  primerConteoEntre(de: DineroContado, desde: number, hasta: number): Promise<ConteoDeDinero | null>;
}

/** Which money a count counted: general cash, or one till's drawer. */
export type DineroContado = { general: true } | { sesionId: string };

/** A count of money the books took (`Reader.primerConteoEntre`). */
export interface ConteoDeDinero {
  id: string;
  lugar: number;
  /** General cash's first count, sent as one: it set where the safe starts, against capital. */
  inicial: boolean;
}

/** The kinds of event that count a till's drawer, and general cash. */
export const CONTEOS_DE_CAJA = ['caja.entregada', 'caja.cerrada', 'jornada.liquidada'] as const;
export const CONTEO_DEL_GENERAL = 'efectivo.arqueo';

/** An event as the log has it (`Reader.eventoDelLog`). */
export interface EventoDelLog {
  /** Its `type`. */
  tipo: string;
  clientTs: string;
  /** Its place in the log (`lugarEnElLog`): what was counted after it already found it. */
  lugar: number;
  payload: unknown;
}

/** One of a till's counts as the log has it (`Reader.conteoDeCaja`). */
export interface ConteoDeCaja {
  tipo: 'caja.abierta' | 'caja.cerrada';
  sesionId: string;
  actorId: string;
  clientTs: string;
  /** Its place in the log (`lugarEnElLog`): what was counted after it already found it. */
  lugar: number;
}

export const saldoId = (operadora: string): string => operadora.trim().toLowerCase();

/**
 * A batch is identified by product, batch number AND location.
 *
 * Location is part of the identity because a batch physically sits somewhere: moving goods to
 * the truck has to move the batch with them, or FEFO at the destination would find nothing and
 * the expiry board would point at the wrong shelf. Receiving the same batch number twice into
 * the same place merges into one row, which is also what a person would expect.
 */
export const loteId = (productoId: string, numero: string, ubicacion: string): string =>
  `${productoId}:${numero.trim()}:${ubicacion}`;

export const existenciaId = (productoId: string, ubicacion: string): string =>
  `${productoId}:${ubicacion}`;

export const precioId = (unidadVentaId: string, listaId: string): string =>
  `${unidadVentaId}:${listaId}`;

/**
 * What a reducer asks to have changed.
 *
 * `upsert` writes a whole row; `patch` merges fields. Both carry `rev`, and an applier must
 * drop a write whose `rev` is lower than the row's current one. That is the entire
 * last-write-wins rule, and keeping it in one place is why it will still be correct in a year.
 */
export type Effect =
  | { kind: 'usuario.upsert'; id: string; row: Usuario }
  | { kind: 'usuario.patch'; id: string; rev: number; patch: Partial<Omit<Usuario, 'id'>> }
  | { kind: 'dispositivo.upsert'; id: string; row: Dispositivo }
  | { kind: 'dispositivo.patch'; id: string; rev: number; patch: Partial<Omit<Dispositivo, 'id'>> }
  | { kind: 'config.set'; clave: string; valor: unknown; rev: number }
  | { kind: 'producto.upsert'; id: string; row: Producto }
  | { kind: 'producto.patch'; id: string; rev: number; patch: Partial<Omit<Producto, 'id'>> }
  | { kind: 'unidadVenta.upsert'; id: string; row: UnidadVenta }
  | { kind: 'unidadVenta.patch'; id: string; rev: number; patch: Partial<Omit<UnidadVenta, 'id'>> }
  | { kind: 'unidadVenta.delete'; id: string; rev: number }
  | { kind: 'listaPrecio.upsert'; id: string; row: ListaPrecio }
  | { kind: 'listaPrecio.patch'; id: string; rev: number; patch: Partial<Omit<ListaPrecio, 'id'>> }
  | { kind: 'precio.set'; id: string; row: Precio }
  | { kind: 'oferta.upsert'; id: string; row: Oferta }
  | { kind: 'oferta.patch'; id: string; rev: number; patch: Partial<Omit<Oferta, 'id'>> }
  | { kind: 'proveedor.upsert'; id: string; row: Proveedor }
  | { kind: 'proveedor.patch'; id: string; rev: number; patch: Partial<Omit<Proveedor, 'id'>> }
  | { kind: 'existencia.set'; id: string; row: Existencia }
  | { kind: 'lote.upsert'; id: string; row: Lote }
  | { kind: 'compra.upsert'; id: string; row: Compra }
  | { kind: 'compra.patch'; id: string; rev: number; patch: Partial<Omit<Compra, 'id'>> }
  | { kind: 'sesion.upsert'; id: string; row: SesionCaja }
  | { kind: 'sesion.patch'; id: string; rev: number; patch: Partial<Omit<SesionCaja, 'id'>> }
  | { kind: 'venta.upsert'; id: string; row: Venta }
  | { kind: 'venta.patch'; id: string; rev: number; patch: Partial<Omit<Venta, 'id'>> }
  | { kind: 'saldo.upsert'; id: string; row: SaldoTelefonico }
  | { kind: 'recarga.upsert'; id: string; row: Recarga }
  | { kind: 'reempaque.upsert'; id: string; row: Reempaque }
  | { kind: 'conteo.upsert'; id: string; row: Conteo }
  | { kind: 'salida.upsert'; id: string; row: Salida }
  | { kind: 'traslado.upsert'; id: string; row: Traslado }
  | { kind: 'cliente.upsert'; id: string; row: Cliente }
  | { kind: 'cliente.patch'; id: string; rev: number; patch: Partial<Omit<Cliente, 'id'>> }
  | { kind: 'pedido.upsert'; id: string; row: Pedido }
  | { kind: 'pedido.patch'; id: string; rev: number; patch: Partial<Omit<Pedido, 'id'>> }
  | { kind: 'jornada.upsert'; id: string; row: Jornada }
  | { kind: 'jornada.patch'; id: string; rev: number; patch: Partial<Omit<Jornada, 'id'>> }
  | { kind: 'gasto.upsert'; id: string; row: Gasto }
  | { kind: 'gasto.patch'; id: string; rev: number; patch: Partial<Omit<Gasto, 'id'>> }
  | { kind: 'movimientoDueno.upsert'; id: string; row: MovimientoDueno }
  | { kind: 'movimientoDueno.patch'; id: string; rev: number; patch: Partial<Omit<MovimientoDueno, 'id'>> }
  | { kind: 'empleado.upsert'; id: string; row: Empleado }
  | { kind: 'empleado.patch'; id: string; rev: number; patch: Partial<Omit<Empleado, 'id'>> }
  | { kind: 'planilla.upsert'; id: string; row: Planilla }
  | { kind: 'planilla.patch'; id: string; rev: number; patch: Partial<Omit<Planilla, 'id'>> }
  | { kind: 'pagoCotizaciones.upsert'; id: string; row: PagoCotizaciones }
  | { kind: 'pagoAguinaldo.upsert'; id: string; row: PagoAguinaldo }
  | { kind: 'pagoAguinaldo.patch'; id: string; rev: number; patch: Partial<Omit<PagoAguinaldo, 'id'>> }
  | { kind: 'abono.upsert'; id: string; row: Abono }
  | { kind: 'abono.patch'; id: string; rev: number; patch: Partial<Omit<Abono, 'id'>> }
  | { kind: 'incobrable.upsert'; id: string; row: Incobrable }
  | { kind: 'incobrable.patch'; id: string; rev: number; patch: Partial<Omit<Incobrable, 'id'>> }
  | { kind: 'deposito.upsert'; id: string; row: Deposito }
  | { kind: 'deposito.patch'; id: string; rev: number; patch: Partial<Omit<Deposito, 'id'>> }
  | { kind: 'transferenciaBanco.upsert'; id: string; row: TransferenciaBanco }
  | { kind: 'transferenciaBanco.patch'; id: string; rev: number; patch: Partial<Omit<TransferenciaBanco, 'id'>> }
  | { kind: 'devolucion.upsert'; id: string; row: Devolucion }
  | { kind: 'devolucionCompra.upsert'; id: string; row: DevolucionCompra }
  | { kind: 'asiento'; entry: JournalEntry };

/** A reducer rejected an event. The event stays in the log; the effect simply does not happen. */
export interface Rejection {
  eventId: string;
  reason: string;
  /** A reducer threw instead of deciding: a bug, refused so that it stops nothing (`applyEvent`). */
  interno?: true;
}

export interface ApplyResult {
  effects: Effect[];
  rejections: Rejection[];
}

export const EMPTY_RESULT: ApplyResult = { effects: [], rejections: [] };
